import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BankAccountFormChatSummaryCard,
  type BankAccountChatSummary,
  type FormChatEditableFieldOption,
  type FormChatFieldInput,
  type FormChatListOption,
} from "gsintegrations";
import { trackFinancialEvent } from "../analytics/financial";
import { CASH_BANK_OPTION_NAME, isCashAccount } from "../constants/accountTypes";
import type { BankAccount } from "../repositories/bankRepository";
import type {
  BankAccountReferenceData,
  FormChatMessageRequest,
  FormChatMessageResponse,
  FormChatState,
} from "../repositories/formChat.repository";
import { formChatRepository } from "../repositories/formChat.repository";
import { mediumRepository } from "../repositories/mediumRepository";
import { terreiroRepository } from "../repositories/terreiroRepository";
import { extractAxiosApiError } from "../utils/extractAxiosApiError";
import { useBankAccountsQuery } from "./useBankAccountsQuery";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { financialQueryKeys } from "./useFinancialQueries";
import { useFormChatSubmitBankAccount } from "./useFormChatSubmitBankAccount";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";

const PIX_KEY_TYPE_REPLIES = [
  "Telefone",
  "CPF",
  "CNPJ",
  "Email",
  "Chave aleatória",
];

const PIX_KEY_TYPE_LABELS: Record<string, string> = {
  PHONE: "Telefone",
  CPF: "CPF",
  CNPJ: "CNPJ",
  EMAIL: "Email",
  RANDOM: "Chave aleatória",
};

const EDITABLE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "bankAccountOptionId", label: "Banco" },
  { key: "agency", label: "Agência" },
  { key: "account", label: "Conta" },
  { key: "pixKeyType", label: "Tipo da chave Pix" },
  { key: "pixKey", label: "Chave Pix" },
  { key: "currentBalance", label: "Saldo atual" },
];

const CASH_EDITABLE_FIELDS = EDITABLE_FIELDS.filter(
  (field) => field.key === "currentBalance",
);

/** `/bank-account-options` hides the cash option, so the create list offers it under a local id the API rejects with the uniqueness message. */
const CASH_OPTION: FormChatListOption = {
  id: "caixa-do-terreiro",
  label: CASH_BANK_OPTION_NAME,
};

const isCashBankAccountRecord = (account: Partial<BankAccount>) =>
  isCashAccount(account.accountType) ||
  account.bankAccountOption?.name === CASH_BANK_OPTION_NAME;

const formatBalance = (amount: unknown) =>
  typeof amount === "number"
    ? amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : undefined;

interface UseBankAccountFormChatOptions {
  enabled: boolean;
  editTargetId?: string;
  confirmLabel: string;
  sessionId?: string;
  formState: FormChatState;
  summary?: BankAccountChatSummary;
  onResponse: (response: FormChatMessageResponse) => void;
  onError?: (error: { code: string; message: string }) => void;
  onReset: () => void;
  onEdit?: () => void;
}

export function useBankAccountFormChatHelpers({
  enabled,
  editTargetId,
  confirmLabel,
  sessionId,
  formState,
  summary,
  onResponse,
  onError,
  onReset,
  onEdit,
}: UseBankAccountFormChatOptions) {
  const productBase = useProductAnalyticsBase();
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};
  const isEdit = Boolean(editTargetId);
  const isCash = formState.accountType === "CASH" || summary?.accountType === "CASH";
  const { data: bankOptionsData } = useBankAccountsQuery({ enabled });
  const { data: ownerAccounts } = useQuery({
    queryKey: terreiroId
      ? financialQueryKeys.bankAccounts(terreiroId)
      : financialQueryKeys.mediumBankAccounts(mediumId),
    queryFn: (): Promise<Array<Partial<BankAccount>>> =>
      terreiroId
        ? terreiroRepository.getTerreiroBankAccounts(terreiroId)
        : (mediumRepository.getBankAccountsByMedium(mediumId as string) as Promise<
            Array<Partial<BankAccount>>
          >),
    enabled: enabled && !isEdit && Boolean(terreiroId || mediumId),
    staleTime: 1000 * 60 * 5,
  });
  const hasCashAccount = (ownerAccounts ?? []).some(isCashBankAccountRecord);
  const {
    submitBankAccountByChat,
    navigateBack,
    canCreate,
    isLimitLoading,
    limitMessage,
    isSubmitting,
  } = useFormChatSubmitBankAccount({ editTargetId, enabled });

  const bankOptions = useMemo<FormChatListOption[]>(
    () =>
      (bankOptionsData ?? [])
        .filter((option) => option.name !== CASH_BANK_OPTION_NAME)
        .map((option) => ({
          id: option.id,
          label: option.name,
          imageUrl: typeof option.image === "string" ? option.image : null,
        })),
    [bankOptionsData],
  );

  const listOptions = useMemo(
    () => (isEdit ? bankOptions : [CASH_OPTION, ...bankOptions]),
    [bankOptions, isEdit],
  );

  const bankAccountReferenceData = useMemo<BankAccountReferenceData>(
    () => ({
      bankOptions: listOptions.map((option) => ({
        id: option.id,
        name: option.label,
      })),
      ...(isEdit ? {} : { hasCashAccount }),
    }),
    [hasCashAccount, isEdit, listOptions],
  );

  const { mutateAsync: sendBankAccountMutation, isPending } = useMutation({
    mutationKey: ["form-chat", "bank-account-message"],
    mutationFn: async (
      payload: FormChatMessageRequest,
    ): Promise<FormChatMessageResponse> => {
      if (terreiroId) return formChatRepository.sendMessage(terreiroId, payload);
      if (mediumId) return formChatRepository.sendPersonalMessage(mediumId, payload);
      throw new Error("Owner scope required");
    },
    onError: () => {
      trackFinancialEvent("financial_form_chat_submit_error", productBase, {
        transaction_type: "bank_account",
      });
    },
  });

  const sendBankAccountRequest = useCallback(
    async (
      payload: Omit<FormChatMessageRequest, "entityType">,
    ): Promise<FormChatMessageResponse | undefined> => {
      if (!terreiroId && !mediumId) return undefined;
      try {
        const response = await sendBankAccountMutation({
          entityType: "bank_account",
          referenceData: bankAccountReferenceData,
          ...(editTargetId
            ? { mode: "edit" as const, targetEntityId: editTargetId }
            : {}),
          ...payload,
        });
        onResponse(response);
        return response;
      } catch (err: unknown) {
        onError?.(extractAxiosApiError(err));
        throw err;
      }
    },
    [
      bankAccountReferenceData,
      editTargetId,
      mediumId,
      onError,
      onResponse,
      sendBankAccountMutation,
      terreiroId,
    ],
  );

  const getFieldOptions = useCallback(
    (field: string | undefined): FormChatListOption[] =>
      field === "bankAccountOptionId" ? listOptions : [],
    [listOptions],
  );

  const isListField = useCallback(
    (field: string | undefined) => field === "bankAccountOptionId",
    [],
  );

  const getFieldInput = useCallback(
    (field: string | undefined): FormChatFieldInput | undefined => {
      if (!field) return undefined;
      if (field === "bankAccountOptionId") {
        return {
          type: "list_select",
          field,
          searchable: true,
          openListLabel: "Ver bancos",
          changeLabel: "Alterar",
          allowFreeText: false,
          topSuggestions: bankOptions.slice(0, 4).map((option) => option.label),
        };
      }
      if (field === "pixKeyType") {
        return {
          type: "quick_replies",
          field,
          quickReplies: PIX_KEY_TYPE_REPLIES,
          allowFreeText: true,
        };
      }
      if (field === "currentBalance") {
        return {
          type: "number",
          field,
          numberFormat: "currency",
          allowNegative: true,
          allowFreeText: true,
          ...(isEdit ? {} : { quickReplies: ["Pular"] }),
        };
      }
      return { type: "text", field, allowFreeText: true };
    },
    [bankOptions, isEdit],
  );

  /** The balance always uses the local input so the chat opens the currency keyboard. */
  const resolveFieldInput = useCallback(
    (
      field: string | undefined,
      serverFieldInput: FormChatFieldInput | undefined,
    ): FormChatFieldInput | undefined =>
      field === "currentBalance"
        ? getFieldInput(field)
        : (serverFieldInput ?? getFieldInput(field)),
    [getFieldInput],
  );

  const editableFieldOptions = useMemo<FormChatEditableFieldOption[]>(() => {
    const pixKeyTypeLabel =
      summary?.pixKeyTypeLabel ??
      (formState.pixKeyType ? PIX_KEY_TYPE_LABELS[formState.pixKeyType] : undefined);
    const currentValues: Record<string, string | undefined> = {
      bankAccountOptionId: summary?.bankName ?? formState.bankName,
      agency: summary?.agency ?? formState.agency,
      account: summary?.account ?? formState.account,
      pixKeyType: pixKeyTypeLabel,
      pixKey: summary?.pixKey ?? formState.pixKey,
      currentBalance: formatBalance(summary?.currentBalance ?? formState.currentBalance),
    };
    return (isCash ? CASH_EDITABLE_FIELDS : EDITABLE_FIELDS).map((field) => ({
      key: field.key,
      label: field.label,
      currentValue: currentValues[field.key],
    }));
  }, [formState, isCash, summary]);

  const handleConfirmBankAccount = useCallback(async () => {
    if (!summary || !sessionId) {
      onError?.({
        code: "FORM_CHAT_INCOMPLETE",
        message: "Revise os dados da conta no chat antes de confirmar.",
      });
      return;
    }
    let response: FormChatMessageResponse | undefined;
    try {
      response = await sendBankAccountRequest({
        sessionId,
        message: "",
        action: "confirm",
      });
    } catch {
      return;
    }
    if (!response || response.currentStep !== "complete") return;

    try {
      await submitBankAccountByChat({ ...formState, ...response.formState });
      onReset();
    } catch (err: unknown) {
      onError?.(
        extractAxiosApiError(
          err,
          "Não foi possível salvar a conta bancária. Revise os dados e tente novamente.",
        ),
      );
    }
  }, [
    formState,
    onError,
    onReset,
    sendBankAccountRequest,
    sessionId,
    submitBankAccountByChat,
    summary,
  ]);

  const transcribeAudio = useCallback(
    async (audio: Blob): Promise<string> => {
      if (terreiroId) {
        return (
          await formChatRepository.transcribeAudio(terreiroId, audio, {
            entityType: "bank_account",
            mode: isEdit ? "edit" : "create",
          })
        ).transcript;
      }
      if (mediumId) {
        return (await formChatRepository.transcribePersonalAudio(mediumId, audio))
          .transcript;
      }
      throw new Error("Owner scope required");
    },
    [isEdit, mediumId, terreiroId],
  );

  const isBusy = isPending || isSubmitting;

  const summaryCard = useMemo(() => {
    if (!summary) return null;
    return (
      <BankAccountFormChatSummaryCard
        summary={summary}
        confirmLabel={confirmLabel}
        isLoading={isBusy}
        onConfirm={handleConfirmBankAccount}
        onEdit={onEdit}
      />
    );
  }, [confirmLabel, handleConfirmBankAccount, isBusy, onEdit, summary]);

  return {
    bankAccountReferenceData,
    sendBankAccountRequest,
    handleConfirmBankAccount,
    summaryCard,
    editableFields: isCash ? CASH_EDITABLE_FIELDS : EDITABLE_FIELDS,
    editableFieldOptions,
    getFieldOptions,
    getFieldInput,
    resolveFieldInput,
    isListField,
    transcribeAudio,
    navigateBack,
    canCreate,
    isLimitLoading,
    limitMessage,
    isPending: isBusy,
  };
}
